import React, {useEffect, useRef, useState} from 'react';
import { Button, Col, Input, PageHeader, Row, Table, Tag, } from 'antd';
import { ReloadOutlined, SearchOutlined, } from '@ant-design/icons';
import _ from 'lodash';
import { getLiftoffUserRoles, getLiftoffUsers } from '../../api/liftoffUserApi';
import { EditRolesButton } from './EditRolesButton';
import { CreateUserButton } from './CreateUserButton';
import { RemoveUserButton } from './RemoveUserButton';
import { useUser } from '../../auth/userHooks';
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";

const LiftoffUsers = () => {
  const user = useUser();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
    },
    {
      title: 'Email',
      defaultSortOrder: 'ascend',
      dataIndex: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Roles',
      render: record => record.roles.map(r => <Tag key={r.role}>{r.role}</Tag>)
    },
    {
      title: 'Actions',
      render: u => (
        <>
          <EditRolesButton user={u} allRoles={roles} onComplete={load} style={{ marginRight: 10 }}/>
          {user && user.email !== u.email && <RemoveUserButton user={u} onComplete={load}/>}
        </>
      )
    }
  ].filter(item => !item.hidden);

  const load = async () => {
    try {
      setLoading(true);
      const users = await getLiftoffUsers();
      setUsers(users);
      filterUsers(users, searchText);

      const roles = await getLiftoffUserRoles();
      setRoles(roles);

    } catch (e) {
      console.error(`Could not get liftoff users`);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = (users, searchText) => {
    setFilteredUsers(
      _.chain(users)
        .filter(
          (u) =>
            searchText === '' ||
            _.includes(_.toLower(u.fullName), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    setSearchText(text);
    filterUsers(users, text);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Liftoff Users"
            subTitle={`(${filteredUsers.length} shown)`}
            style={{padding: 0}}
          />
        </Col>

      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={6} style={{marginRight: 10}}>
          <Input
            placeholder="Search for users(s) by name"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto">
          <Button
            style={{float: 'right'}}
            onClick={() => load()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          <CreateUserButton allRoles={roles} onComplete={load} style={{float: 'right', marginRight: 10}}/>
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="email"
        dataSource={filteredUsers}
        columns={columns}
        pagination={getPaginationConfig(filteredUsers.length)}
        size="small"
        loading={loading}
        scroll={{y:tableHeight}}
      />
    </>
  );
};

export { LiftoffUsers };
